// Global auth gate — mounted inside ConvexAuthProvider in main.tsx.
// Mirror of src/app/lib/auth/AuthGate.tsx (mobile). Web-specific bits:
//   • Uses react-router-dom's useLocation + useNavigate (no expo-router).
//   • Public-route allowlist matches main.tsx's URL paths (which start
//     with /auth/... rather than mobile's /signin /verify etc).
//
// See the mobile AuthGate header comment for the design rationale.

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useConvexAuth } from 'convex/react';

// Routes that DON'T require an authenticated user. Everything else
// requires auth + bounces to /auth/signin on isAuthenticated=false.
const PUBLIC_PREFIXES: ReadonlyArray<string> = [
  '/auth/signin',
  '/auth/signup',
  '/auth/forgot',
  '/auth/reset',
  '/auth/verify',
  '/auth/oauth/',
  '/p/',
  // Pricing page — public like every other marketing surface; only the
  // subscribe CTA requires auth (Pricing routes signed-out clicks to
  // /auth/signin itself, with returnTo back into the subscribe flow).
  '/pricing',
  // Server-rendered by Pages Functions; listed so a stray client-side
  // navigation to /blog never bounces to sign-in.
  '/blog',
  '/privacy',
  '/terms',
  '/child-safety',
  '/get',
  // Agent-platform marketing page — public, standalone (own nav/footer).
  '/agents',
  // Referral landing — must stay public so a logged-out friend isn't
  // bounced to /auth/signin before JoinReferralPage captures the code
  // and routes them onward to signup. See routes/JoinReferral.
  '/join',
  '/ref/',
  '/account/delete',
  '/account/restore',
  // Agent-platform out-of-band approval landing — magic-link token IS the
  // auth (no sign-in). Distinct from '/agents' (marketing) which /agent/…
  // does not prefix-match. See routes/AgentApprove.
  '/agent/approve',
];

function isPublicRoute(pathname: string): boolean {
  if (!pathname || pathname === '/' || pathname === '') return true;
  return PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
}

export function AuthGate({ children }: { children: React.ReactNode }): React.ReactElement {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) return;
    if (isPublicRoute(location.pathname)) return;
    // Preserve where the user was headed so sign-in can send them back
    // there (e.g. the /connect consent page carries its ?state/&scopes
    // through login). SignIn reads `returnTo` and navigates to it once
    // isAuthenticated flips. Falls back to /home when absent, so the
    // default landing is unchanged for anyone bounced from elsewhere.
    const returnTo = location.pathname + location.search;
    navigate(`/auth/signin?returnTo=${encodeURIComponent(returnTo)}`, {
      replace: true,
    });
  }, [isAuthenticated, isLoading, location.pathname, location.search, navigate]);

  return <>{children}</>;
}
